import { Link } from 'react-router-dom'

export type FooterLinkItem = {
  label: string
  to: string
  isExternal?: boolean
}

const footerLinks = {
  services: [
    { label: 'Airport Transfers', to: '/booking' },
    { label: 'Day Trips', to: '/day-trips' },
    { label: 'Ski Transfers', to: '/ski-transfers' },
    { label: 'Private Van Tours', to: '/private-tours' },
  ],
  features: [
    { label: 'Home', to: '/' },
    { label: 'About Us', to: '/about' },
    { label: 'Our Services', to: '/#services' },
    { label: 'Our Fleet', to: '/fleets' },
  ],
  support: [
    { label: 'Help & Contact', to: 'mailto:office@salzburgcab.com', isExternal: true },
    { label: 'FAQ', to: '/#faq' },
  ],
  legal: [
    { label: 'Privacy Policy', to: '/privacy-policy' },
    { label: 'Terms of Service', to: '/terms-of-service' },
    { label: 'Cancellation Policy', to: '/cancellation-policy' },
  ],
} as const

const socialLinks = [
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/',
    icon: (
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M22 12.07C22 6.48 17.52 2 11.93 2S1.86 6.48 1.86 12.07c0 5.02 3.66 9.18 8.44 9.93v-7.02H7.9v-2.91h2.4V9.85c0-2.37 1.41-3.68 3.57-3.68 1.03 0 2.11.18 2.11.18v2.32h-1.19c-1.17 0-1.54.73-1.54 1.48v1.78h2.62l-.42 2.91h-2.2V22c4.78-.75 8.44-4.91 8.44-9.93z" />
      </svg>
    ),
  },
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/',
    icon: (
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7zm11 1.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
      </svg>
    ),
  },
  {
    name: 'Twitter',
    href: 'https://twitter.com/',
    icon: (
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.727-8.835L2.25 2.25h6.093l4.261 5.677L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
      </svg>
    ),
  },
  {
    name: 'YouTube',
    href: 'https://www.youtube.com/',
    icon: (
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.5 31.5 0 0 0 0 12a31.5 31.5 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31.5 31.5 0 0 0 24 12a31.5 31.5 0 0 0-.5-5.8zM9.8 15.5v-7l6.2 3.5-6.2 3.5z" />
      </svg>
    ),
  },
] as const

export default function Footer() {
  return (
    <>
      {/* =====================================================
          MOBILE FOOTER ONLY
          md:hidden = does NOT affect desktop
      ===================================================== */}
      <div className=" px-[13px] pb-[18px] pt-[16px] md:hidden">
        <footer
          className="
            w-full
            rounded-[30px]
            bg-white
            px-[25px]
            pb-[20px]
            pt-[20px]
            shadow-[0_2px_20px_rgba(0,0,0,0.05)]
          "
        >
          {/* Logo */}
          <img
            src="/assets/images/logo.png"
            alt="Salzburg Cab"
            className="h-auto w-[135px] object-contain"
          />

          {/* Description */}
          <p className="mt-[17px] max-w-[310px] font-host text-[12px] font-normal leading-[1.5] text-[#4C4B4B]">
            Reliable airport transfers, private day trips, and
            comfortable transportation across Austria and
            neighboring destinations.
          </p>

          {/* Links */}
          <div className="mt-[22px] grid grid-cols-2 items-start gap-x-[30px] gap-y-[17px]">
            {/* Services */}
            <MobileFooterColumn
              title="Services"
              links={footerLinks.services}
            />

            {/* Features */}
            <MobileFooterColumn
              title="Features"
              links={footerLinks.features}
            />

            {/* Support */}
            <MobileFooterColumn
              title="Support"
              links={footerLinks.support}
            />

            {/* Legal */}
            <MobileFooterColumn
              title="Legal"
              links={footerLinks.legal}
            />
          </div>

          {/* Copyright */}
         {/* Copyright + Developer Credit */}
        <div className="mt-[22px] font-host text-[11px] font-normal leading-[1.5] text-[#696262]">
          <p>© 2026 Salzburg Cab. All Rights Reserved.</p>

          <p className="mt-[5px]">
            Powered by{' '}
            <a
              href="https://www.cryoflametechnologies.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap font-semibold text-[#696262]  transition-colors duration-200 hover:text-[#FF6A1A] active:text-[#FF6A1A]"
            >
              Cryoflame Technologies LLP
            </a>
            .
          </p>
        </div>
          {/* Social */}
          <div className="mt-[25px] pl-[17px]">
            <p className="font-host text-[16px] font-medium leading-none text-[#404040]">
              Follow Us on
            </p>

            <div className="mt-[9px] flex items-center gap-[8px]">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.name}
                  title={social.name}
                  className="
                    grid
                    h-[26px]
                    w-[26px]
                    shrink-0
                    place-items-center
                    rounded-full
                    bg-[#131313]
                    text-white
                    transition-transform
                    duration-200
                    active:scale-95
                  "
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
        </footer>
      </div>

      <div className="hidden md:block">
        <footer className="relative overflow-hidden rounded-t-[64px] bg-white shadow-[0_-8px_30px_rgba(65,86,75,0.10)] lg:rounded-t-[72px]">
          <div className="mx-auto w-full max-w-[1320px] px-6 pb-6 pt-[62px] lg:px-8">
<div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-[310px_150px_120px_100px_155px_230px] lg:items-start lg:justify-between lg:gap-x-8">
              <div className="flex h-full flex-col">
                <img
                  src="/assets/images/logo.png"
                  alt="Salzburg Cab"
                  className="h-[58px] w-auto self-start object-contain"
                />

                <p className="mt-4 max-w-[290px] font-host text-[12px] font-normal leading-[1.5] text-[#4C4B4B]">
                  Reliable airport transfers, private day trips,
                  and comfortable transportation across Austria
                  and neighboring destinations.
                </p>

              <p className="mt-auto whitespace-nowrap pt-[54px] font-host text-[11px] font-medium leading-none text-[#696262]">
                © 2026 Salzburg Cab. All Rights Reserved. Powered by{' '}
                <a
                  href="https://www.cryoflametechnologies.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-[#696262] transition-colors duration-200 hover:text-[#FF6A1A]"
                >
                  Cryoflame Technologies LLP
                </a>
                .
              </p>
              </div>

              <FooterColumn
                title="Services"
                links={footerLinks.services}
              />

              <FooterColumn
                title="Features"
                links={footerLinks.features}
              />

              <FooterColumn
                title="Support"
                links={footerLinks.support}
              />

              <FooterColumn
                title="Legal"
                links={footerLinks.legal}
              />

              {/* Salzburg Cab Contact + Social */}
<div className="shrink-0">
  <h4 className="mb-[15px] whitespace-nowrap font-host text-[13px] font-semibold leading-none text-black">
    Salzburg Cab
  </h4>

  <div className="flex flex-col gap-[13px]">
    {/* Address */}
    <a
      href="https://goo.gl/maps/roMzLFeNML96RJBQA"
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-[8px] font-host text-[11px] font-normal leading-[1.35] text-[#5F5A5A] transition-colors hover:text-brand-orange group"
      title="View on Google Maps"
    >
      <img src="assets/images/f-location.png" alt="Location" />

      <p className="group-hover:underline">
        Rudolf Spängler Straße 10,
        <br />
        5020 Salzburg, Austria
      </p>
    </a>

    {/* Phone 1 */}
    <a
      href="tel:+43720775053"
      className="flex items-center gap-[8px] font-host text-[11px] font-normal text-[#5F5A5A] transition-colors hover:text-brand-orange"
    >
      <img src="assets/images/f-call.png" alt="Phone" />

      <span>+43 720 7750 53</span>
    </a>

    {/* Phone 2 */}
    <a
      href="tel:+436763347141"
      className="flex items-center gap-[8px] font-host text-[11px] font-normal text-[#5F5A5A] transition-colors hover:text-brand-orange"
    >
      <img src="assets/images/f-call.png" alt="Mobile" />

      <span>+43 676 334 7141</span>
    </a>

    {/* Office email */}
    <a
      href="mailto:office@salzburgcab.com"
      className="flex items-center gap-[8px] font-host text-[11px] font-normal text-[#5F5A5A] transition-colors hover:text-brand-orange"
    >
      <img src="assets/images/f-mail.png" alt="Office Email" />

      <span>office@salzburgcab.com</span>
    </a>

    {/* Info email */}
    <a
      href="mailto:info@salzburgcab.com"
      className="flex items-center gap-[8px] font-host text-[11px] font-normal text-[#5F5A5A] transition-colors hover:text-brand-orange"
    >
      <img src="assets/images/f-mail.png" alt="Info Email" />

      <span>info@salzburgcab.com</span>
    </a>
  </div>

  {/* Follow Us */}
  <div className="mt-[34px]">
    <p className="mb-[12px] whitespace-nowrap text-left font-host text-[13px] font-semibold leading-none text-[#404040]">
      Follow Us on
    </p>

    <div className="flex items-center justify-start gap-[8px]">
      {socialLinks.map((social) => (
        <a
          key={social.name}
          href={social.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={social.name}
          title={social.name}
          className="
            grid
            h-[25px]
            w-[25px]
            shrink-0
            place-items-center
            rounded-full
            bg-[#131313]
            text-white
            transition-all
            duration-200
            hover:-translate-y-0.5
            hover:bg-[#FF6A1A]
          "
        >
          {social.icon}
        </a>
      ))}
    </div>
  </div>
</div>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}

type FooterColumnProps = {
  title: string
  links: readonly FooterLinkItem[]
}

/* =========================================================
   DESKTOP COLUMN
========================================================= */
function FooterColumn({
  title,
  links,
}: FooterColumnProps) {
  return (
    <div className="shrink-0">
      <h4 className="mb-[13px] whitespace-nowrap font-host text-[13px] font-semibold leading-none text-black">
        {title}
      </h4>

      <ul className="flex flex-col gap-[11px]">
        {links.map((link) => (
          <li key={link.label} className="leading-none">
            {link.isExternal || link.to.startsWith('mailto:') || link.to.startsWith('tel:') ? (
              <a
                href={link.to}
                className="whitespace-nowrap font-host text-[12px] font-normal leading-none text-[#5F5A5A] transition-colors duration-200 hover:text-brand-orange"
              >
                {link.label}
              </a>
            ) : (
              <Link
                to={link.to}
                className="whitespace-nowrap font-host text-[12px] font-normal leading-none text-[#5F5A5A] transition-colors duration-200 hover:text-brand-orange"
              >
                {link.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

/* =========================================================
   MOBILE COLUMN
========================================================= */
function MobileFooterColumn({
  title,
  links,
}: FooterColumnProps) {
  return (
    <div className="min-w-0">
      <h4 className="mb-[12px] font-host text-[16px] font-semibold leading-none text-[#111111]">
        {title}
      </h4>

      <ul className="flex flex-col gap-[10px]">
        {links.map((link) => (
          <li key={link.label} className="leading-none">
            {link.isExternal || link.to.startsWith('mailto:') || link.to.startsWith('tel:') ? (
              <a
                href={link.to}
                className="font-host text-[12px] font-normal leading-none text-[#624747] transition-colors hover:text-brand-orange"
              >
                {link.label}
              </a>
            ) : (
              <Link
                to={link.to}
                className="font-host text-[12px] font-normal leading-none text-[#624747] transition-colors hover:text-brand-orange"
              >
                {link.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}